'use client';

import type { Entitlements } from '@billing/entitlements';
import { FEATURES, type FeatureId } from '@billing/plans';
import { createContext, FC, ReactNode, useContext, useMemo } from 'react';

type EntitlementsContextValue = Entitlements & {
  has: (feature: FeatureId) => boolean;
  seatsRemaining: number | null;
  atSeatLimit: boolean;
};

const EntitlementsContext = createContext<EntitlementsContextValue | null>(
  null
);

/**
 * Per-organization plan entitlements computed on the server (org layout) and
 * shared with client components for buttons, CTAs and pre-checks. Server
 * gates remain authoritative; this only shapes the UI.
 */
export const EntitlementsProvider: FC<{
  value: Entitlements;
  children: ReactNode;
}> = ({ value, children }) => {
  const context = useMemo<EntitlementsContextValue>(() => {
    const remaining =
      value.seatLimit == null
        ? null
        : Math.max(0, value.seatLimit - value.seatsUsed);
    return {
      ...value,
      has: feature => value.features.includes(feature),
      seatsRemaining: remaining,
      atSeatLimit: remaining === 0
    };
  }, [value]);
  return (
    <EntitlementsContext.Provider value={context}>
      {children}
    </EntitlementsContext.Provider>
  );
};

export function useEntitlements(): EntitlementsContextValue {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) {
    throw new Error(
      'useEntitlements must be used within an EntitlementsProvider'
    );
  }
  return ctx;
}

/** Non-throwing variant for components that also render outside an org. */
export function useOptionalEntitlements(): EntitlementsContextValue | null {
  return useContext(EntitlementsContext);
}

export { FEATURES };
